"use client";

/**
 * RunFirstCta — the shared "audit-entry" CTA for the marketing surface.
 * It removes the login wall for guests:
 *
 *   - Guests (and while auth is still loading): clicking opens a compact
 *     "paste a URL → Audit my site" popover that runs a real guest audit
 *     with no sign-up. It reuses <HeroUrlForm/>, so guests land on /try
 *     (run-before-signup) exactly like the homepage hero bar.
 *   - Signed-in users: a plain link straight to the /audit/new configurator.
 *
 * Why it exists: every audit-entry CTA except the homepage hero used to
 * link to /audit/new, which the app shell redirects to /login for guests
 * (see app-shell.tsx). So a cold visitor — ~85% of them on mobile, from
 * Reddit — was asked to sign in before seeing any value. This makes
 * run-first the default everywhere a guest can act, without moving any
 * hero layout or secondary CTA.
 *
 * Scope guard: purchase CTAs ("Get Pro", top-up "Buy") are *buy* actions
 * and keep routing to checkout — they are intentionally NOT routed through
 * this component.
 */

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { useAccountState } from "./use-account-state";
import { track } from "../lib/track";
import { usePathname } from "next/navigation";
import { HeroUrlForm } from "../(marketing)/hero-url-form";

interface RunFirstCtaProps {
  /** Button/link className — defaults to the standard primary button. */
  className?: string;
  /** Label shown to guests (and while auth is loading). */
  guestLabel?: string;
  /** Label for signed-in users. */
  authedLabel?: string;
  /** Where signed-in users go. */
  authedHref?: string;
  /**
   * Fired when the guest popover opens, or when the signed-in link is
   * clicked. The mobile header uses it to close its menu first.
   */
  onActivate?: () => void;
  surface?: string;
}

export function RunFirstCta({
  className = "lp-btn lp-btn-primary",
  guestLabel = "Start a free audit",
  authedLabel = "Start a new audit",
  authedHref = "/audit/new",
  onActivate,
  surface = "marketing",
}: RunFirstCtaProps) {
  const state = useAccountState();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  // The header survives route changes: close its entry dialog on /try.
  useEffect(() => { setOpen(false); }, [pathname]);

  // While open: Escape closes, and the URL field takes focus so a visitor
  // can paste-and-go immediately (no extra tap on mobile).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    popoverRef.current?.querySelector("input")?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Signed-in users skip the wall entirely — straight to the configurator.
  if (state.status === "authenticated") {
    return (
      <Link className={className} href={authedHref} onClick={onActivate}>
        {authedLabel}
      </Link>
    );
  }

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={() => {
          onActivate?.();
          setOpen(true);
          track("Guest CTA Opened", { surface });
        }}
      >
        {guestLabel}
      </button>
      {open ? (
        <div
          className="rf-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Audit any website free"
        >
          <button
            type="button"
            className="rf-backdrop"
            aria-label="Close"
            onClick={() => setOpen(false)}
          />
          <div className="rf-popover" ref={popoverRef}>
            <button
              type="button"
              className="rf-close"
              aria-label="Close"
              onClick={() => setOpen(false)}
            >
              ×
            </button>
            <p className="lp-kicker">Audit any site free</p>
            <h2 className="rf-title">Paste a URL to start</h2>
            <p className="rf-sub">
              Find usability and accessibility issues on your site.
              Preview up to 3 findings, then create a free account for the full report.
            </p>
            <HeroUrlForm buttonLabel="Check my site free" surface={surface} />
            <p className="rf-note">No signup to start. No card needed.</p>
          </div>
        </div>
      ) : null}
    </>
  );
}
